import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  Fade,
  Slide,
  Zoom,
  Grow,
  keyframes,
  useTheme,
  useMediaQuery,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import czechTranslations from '../../translations/czech';

// 🎨 Animation keyframes
const floatingAnimation = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-30px) rotate(120deg); }
  66% { transform: translateY(-60px) rotate(240deg); }
  100% { transform: translateY(0px) rotate(360deg); }
`;

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 20px rgba(16, 185, 129, 0.3); }
  50% { box-shadow: 0 0 40px rgba(16, 185, 129, 0.6), 0 0 60px rgba(16, 185, 129, 0.4); }
  100% { box-shadow: 0 0 20px rgba(16, 185, 129, 0.3); }
`;

const successPulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // 🎭 Animation states
  const [showContent, setShowContent] = useState(false);
  const [showForm, setShowForm] = useState(false);
  
  // 📝 Form state
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [userInfo, setUserInfo] = useState<{ email: string; firstName: string } | null>(null);

  // Password strength calculation
  const calculatePasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[a-z]/.test(password)) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    return strength;
  };

  const passwordStrength = calculatePasswordStrength(formData.newPassword);
  const getPasswordStrengthColor = () => {
    if (passwordStrength < 25) return '#ef4444';
    if (passwordStrength < 50) return '#f59e0b';
    if (passwordStrength < 75) return '#eab308';
    return '#10b981';
  };

  useEffect(() => {
    // 🔍 Validate token on mount
    const validateToken = async () => {
      if (!token) {
        setTokenValid(false);
        setError(czechTranslations.auth.resetPassword.errors.invalidToken);
        return;
      }

      try {
        const response = await axios.get(`/password-reset/validate-reset-token/${token}`);
        
        if (response.data.valid) {
          setTokenValid(true);
          setUserInfo({
            email: response.data.email,
            firstName: response.data.firstName,
          });
        } else {
          setTokenValid(false);
          setError(response.data.error || czechTranslations.auth.resetPassword.errors.invalidToken);
        }
      } catch (err: any) {
        setTokenValid(false);
        setError(czechTranslations.auth.resetPassword.errors.invalidToken);
      }
    };

    validateToken();
  }, [token]);

  useEffect(() => {
    // 🎪 Staggered entrance animations
    if (tokenValid !== null) {
      setTimeout(() => setShowContent(true), 200);
      setTimeout(() => setShowForm(true), 600);
    }
  }, [tokenValid]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { newPassword, confirmPassword } = formData;

    if (!newPassword || !confirmPassword) {
      setError(czechTranslations.auth.resetPassword.errors.passwordRequired);
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(czechTranslations.auth.resetPassword.errors.passwordMismatch);
      setLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError(czechTranslations.auth.resetPassword.errors.weakPassword);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post('/password-reset/reset-password', {
        token,
        newPassword,
        confirmPassword,
      });

      if (response.data.success) {
        setSuccess(true);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || czechTranslations.auth.resetPassword.errors.serverError;
      setError(errorMessage);
    }

    setLoading(false);
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    if (error) setError(''); // Clear error when user starts typing
  };

  if (tokenValid === false) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={24}
            sx={{
              p: 4,
              borderRadius: 4,
              textAlign: 'center',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
            }}
          >
            <Typography variant="h4" sx={{ color: 'error.main', mb: 2, fontWeight: 700 }}>
              ❌ Neplatný odkaz
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              {error || 'Odkaz pro obnovení hesla je neplatný nebo vypršel.'}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
              startIcon={<ArrowBackIcon />}
              sx={{ mr: 2 }}
            >
              Zpět na přihlášení
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/forgot-password')}
            >
              Požádat o nový odkaz
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (tokenValid === null) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CircularProgress size={60} sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background: success 
          ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #6366f1 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 0.8s ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 50%)',
          zIndex: 1,
        }
      }}
    >
      {/* ✨ Floating animated elements */}
      {[...Array(6)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: { xs: '60px', md: '80px' },
            height: { xs: '60px', md: '80px' },
            borderRadius: '50%',
            background: `linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 100%)`,
            animation: `${floatingAnimation} ${6 + i * 2}s ease-in-out infinite`,
            animationDelay: `${i * 0.5}s`,
            zIndex: 1,
            top: `${20 + Math.random() * 60}%`,
            left: `${10 + Math.random() * 80}%`,
          }}
        />
      ))}

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
        <Fade in={showContent} timeout={800}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            {/* 🔒 Security Icon */}
            <Zoom in={showContent} timeout={1000} style={{ transitionDelay: '200ms' }}>
              <Box
                sx={{
                  width: { xs: 80, md: 120 },
                  height: { xs: 80, md: 120 },
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  animation: success ? `${pulseGlow} 3s ease-in-out infinite` : 'none',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                }}
              >
                {success ? (
                  <CheckCircleIcon 
                    sx={{ 
                      fontSize: { xs: 40, md: 60 }, 
                      color: 'white',
                      animation: `${successPulse} 1s ease-in-out infinite`,
                    }} 
                  />
                ) : (
                  <SecurityIcon sx={{ fontSize: { xs: 40, md: 60 }, color: 'white' }} />
                )}
              </Box>
            </Zoom>

            {/* 🎨 Animated title */}
            <Typography
              variant="h2"
              sx={{
                color: 'white',
                fontWeight: 800,
                mb: 2,
                background: success 
                  ? 'linear-gradient(45deg, #ffffff 30%, #f0fff4 90%)'
                  : 'linear-gradient(45deg, #ffffff 30%, #f0f9ff 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 0 30px rgba(255,255,255,0.5)',
                transition: 'all 0.5s ease',
              }}
            >
              {success ? czechTranslations.auth.resetPassword.success.title : czechTranslations.auth.resetPassword.title}
            </Typography>
            
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'rgba(255,255,255,0.9)', 
                fontWeight: 400,
                mb: 4,
                transition: 'all 0.5s ease',
              }}
            >
              {success 
                ? czechTranslations.auth.resetPassword.success.message
                : `${czechTranslations.auth.resetPassword.subtitle} ${userInfo?.firstName || ''}`
              }
            </Typography>
          </Box>
        </Fade>

        <Slide in={showForm} direction="up" timeout={800}>
          <Paper
            elevation={24}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            }}
          >
            {success ? (
              // ✅ Success State
              <Grow in={success} timeout={600}>
                <Box sx={{ textAlign: 'center' }}>
                  <CheckCircleIcon 
                    sx={{ 
                      fontSize: 64, 
                      color: 'success.main', 
                      mb: 2,
                      animation: `${successPulse} 2s ease-in-out infinite`,
                    }} 
                  />
                  
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: 'success.main' }}>
                    {czechTranslations.auth.resetPassword.success.title}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    {czechTranslations.auth.resetPassword.success.instruction}
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/login')}
                    startIcon={<LoginIcon />}
                    sx={{
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(16, 185, 129, 0.4)',
                      },
                    }}
                  >
                    {czechTranslations.auth.resetPassword.success.loginButton}
                  </Button>
                </Box>
              </Grow>
            ) : (
              // 📝 Form State
              <Box>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
                    {czechTranslations.auth.resetPassword.title}
                  </Typography>
                  
                  {userInfo && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Pro účet: {userInfo.email}
                    </Typography>
                  )}
                </Box>

                {error && (
                  <Grow in={!!error} timeout={500}>
                    <Alert 
                      severity="error" 
                      sx={{ 
                        mb: 3,
                        borderRadius: 2,
                        '& .MuiAlert-message': {
                          fontWeight: 500
                        }
                      }}
                    >
                      {error}
                    </Alert>
                  </Grow>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                  <Grow in={showForm} timeout={600} style={{ transitionDelay: '200ms' }}>
                    <TextField
                      fullWidth
                      label={czechTranslations.auth.resetPassword.newPassword}
                      type={showPassword ? 'text' : 'password'}
                      value={formData.newPassword}
                      onChange={handleInputChange('newPassword')}
                      margin="normal"
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="primary" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          transition: 'all 0.3s ease',
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
                          },
                          '&.Mui-focused': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 20px rgba(99, 102, 241, 0.2)',
                          },
                        },
                      }}
                    />
                  </Grow>

                  {/* Password Strength Indicator */}
                  {formData.newPassword && (
                    <Grow in={!!formData.newPassword} timeout={400}>
                      <Box sx={{ mt: 1, mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Síla hesla:
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: getPasswordStrengthColor(),
                              fontWeight: 600,
                            }}
                          >
                            {passwordStrength < 25 && 'Slabé'}
                            {passwordStrength >= 25 && passwordStrength < 50 && 'Střední'}
                            {passwordStrength >= 50 && passwordStrength < 75 && 'Dobré'}
                            {passwordStrength >= 75 && 'Silné'}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={passwordStrength}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: 'rgba(0,0,0,0.1)',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: getPasswordStrengthColor(),
                              borderRadius: 3,
                            },
                          }}
                        />
                      </Box>
                    </Grow>
                  )}

                  <Grow in={showForm} timeout={600} style={{ transitionDelay: '300ms' }}>
                    <TextField
                      fullWidth
                      label={czechTranslations.auth.resetPassword.confirmPassword}
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={handleInputChange('confirmPassword')}
                      margin="normal"
                      required
                      error={Boolean(formData.confirmPassword && formData.newPassword !== formData.confirmPassword)}
                      helperText={
                        formData.confirmPassword && formData.newPassword !== formData.confirmPassword
                          ? 'Hesla se neshodují'
                          : ''
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="primary" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          transition: 'all 0.3s ease',
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
                          },
                          '&.Mui-focused': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 20px rgba(99, 102, 241, 0.2)',
                          },
                        },
                      }}
                    />
                  </Grow>

                  <Grow in={showForm} timeout={600} style={{ transitionDelay: '400ms' }}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={loading || passwordStrength < 50 || formData.newPassword !== formData.confirmPassword}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SecurityIcon />}
                      sx={{
                        mt: 3,
                        mb: 2,
                        py: 1.5,
                        borderRadius: 3,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                          transition: 'left 0.5s',
                        },
                        '&:hover::before': {
                          left: '100%',
                        },
                        '&:hover': {
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(99, 102, 241, 0.4)',
                        },
                        '&:disabled': {
                          background: 'rgba(99, 102, 241, 0.3)',
                          transform: 'none',
                        },
                      }}
                    >
                      {loading ? czechTranslations.common.loading.default : czechTranslations.auth.resetPassword.resetButton}
                    </Button>
                  </Grow>

                  <Grow in={showForm} timeout={600} style={{ transitionDelay: '500ms' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Button
                        variant="text"
                        onClick={() => navigate('/login')}
                        startIcon={<ArrowBackIcon />}
                        sx={{
                          color: 'primary.main',
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: 'rgba(99, 102, 241, 0.05)',
                          },
                        }}
                      >
                        {czechTranslations.auth.resetPassword.backToLogin}
                      </Button>
                    </Box>
                  </Grow>
                </Box>
              </Box>
            )}
          </Paper>
        </Slide>
      </Container>
    </Box>
  );
};

export default ResetPassword;
